// src/components/personal/sections/SettingsSection/AppSettings/settings.ts
import { api } from '@/services/api';

// Define the interfaces here since they are missing from the imported module
export interface SaveResult {
  success: boolean;
  message?: string;
  error?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Проверяет, есть ли изменения между текущим и оригинальным объектами
 */
export function hasChanges<T>(current: T, original: T): boolean {
  return JSON.stringify(current) !== JSON.stringify(original);
}

/**
 * Возвращает объект, содержащий только измененные поля
 */
// Добавьте дополнительное логирование в функцию getChangedValues
export function getChangedValues<T extends Record<string, any>>(current: T, original: T): Partial<T> {
  const changes: Partial<T> = {};
  
  Object.keys(current).forEach(key => {
    const typedKey = key as keyof T;
    // Используем строгое неравенство для булевых значений
    if (current[typedKey] !== original[typedKey]) {
      changes[typedKey] = current[typedKey];
      console.log(`Поле ${key} изменено: ${original[typedKey]} -> ${current[typedKey]} (тип: ${typeof current[typedKey]})`);
    }
  });
  
  console.log('Отправляемые изменения:', changes);
  return changes;
}

/**
 * Преобразует дату из ISO строки в локальный формат
 */
export function formatDate(dateString?: string, locale: string = 'ru-RU'): string {
  if (!dateString) return 'Не указано';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return 'Неверный формат даты';
  }
}

/**
 * Инициализирует настройки из данных пользователя с значениями по умолчанию
 */
export function initializeSettings<T extends Record<string, any>>(
  userData: Record<string, any> | null | undefined, 
  defaults: T
): T {
  if (!userData) return { ...defaults };
  
  console.log('Инициализация настроек из данных пользователя:', userData);
  
  const result = { ...defaults };
  
  // Копируем значения из userData в result, если они существуют
  Object.keys(defaults).forEach(key => {
    const typedKey = key as keyof T;
    if (userData[key] !== undefined && userData[key] !== null) {
      result[typedKey] = userData[key];
      console.log(`- Установлено поле ${key}: ${userData[key]}`);
    } else {
      console.log(`- Используется значение по умолчанию для ${key}: ${defaults[typedKey]}`);
    }
  });
  
  return result;
}

/**
 * Общая функция для сохранения настроек через API
 */
export async function saveSettings<T>(
  endpoint: string, 
  data: T
): Promise<SaveResult> {
  try {
    console.log('Сохранение настроек. Данные:', data);
    
    let response;
    
    // Если эндпоинт не указан, используем эндпоинт обновления профиля
    if (!endpoint) {
      // Используем POST запрос для обновления профиля
      response = await api.post(api.endpoints.auth.updateProfile, data);
    } else {
      // Для специфичных эндпоинтов
      const url = endpoint.startsWith('/') 
        ? endpoint 
        : `${api.endpoints.auth.profile}${endpoint}`;
      response = await api.post(url, data);
    }
    
    if (response && response.success) {
      console.log('Настройки успешно сохранены:', response);
      return { 
        success: true,
        message: response.message || 'Settings updated successfully'
      };
    } else if (response && response.error) {
      throw new Error(response.error);
    }
    
    return { success: true };
  } catch (err: any) {
    console.error(`Error saving settings:`, err);
    return { 
      success: false, 
      error: err.message || 'Unknown error'
    };
  }
}

/**
 * Функция для получения настроек через API
 */
export async function fetchSettings<T>(
  endpoint: string
): Promise<ApiResponse<T>> {
  try {
    const data = await api.get<T>(`${endpoint}`);
    console.log(`Получены настройки из ${endpoint}:`, data);
    return { 
      success: true,
      data
    };
  } catch (err: any) {
    console.error(`Error fetching settings from ${endpoint}:`, err);
    return {
      success: false,
      error: err.message || 'Unknown error'
    };
  }
}

/**
 * Функция для смены пароля
 */
export async function changePassword(
  userId: number,
  currentPassword: string,
  newPassword: string
): Promise<SaveResult> {
  try {
    await api.post(`/api/business/${userId}/change-password/`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    
    return { 
      success: true,
      message: 'Password changed successfully'
    };
  } catch (err: any) {
    console.error('Error changing password:', err);
    return {
      success: false,
      error: err.message || 'Failed to change password'
    };
  }
}